import { useMemo } from 'react'
import useSWR from 'swr'

import { apiFetcher } from '@/data/fetcher'
import { DashboardStats } from '@/interfaces/DashboardStats'
import { Invoice } from '@/interfaces/Invoice'
import { Money } from '@invo.space/shared'

/** Všechny částky v haléřích. Náklady kladně. */
export interface MonthRow {
  date: Date
  paid: number
  unpaid: number
  receipts: number
  expenses: number
  /** zaplacené faktury + pokladna */
  income: number
  /** příjmy minus náklady (stejný výpočet jako dřív "Výsledek") */
  result: number
  paidPrev: number
  unpaidPrev: number
}

export type Period = 'month' | '30d' | 'year' | '12m'

export const periodOptions: { value: Period; label: string }[] = [
  { value: 'month', label: 'Tento měsíc' },
  { value: '30d', label: '30 dní' },
  { value: 'year', label: 'Letos' },
  { value: '12m', label: '12 měsíců' },
]

const amount = (m: Money | undefined | null) => Number(m?.amount ?? 0)

/** 13 měsíců od (aktuální - 12) po aktuální, vždy první den v měsíci (žádné přetékání 31. dne). */
export const monthWindow = (now = new Date()) =>
  Array.from({ length: 13 }, (_, i) => new Date(now.getFullYear(), now.getMonth() - 12 + i, 1))

interface CashSummary {
  total: { total: Money; totalTax: Money; totalTaxFree: Money }
}

const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`

/** Starší backend bez monthly-summaries: jeden požadavek na každý měsíc. */
const fetchMonthByMonth = (months: Date[]) =>
  Promise.all(
    months.map((d) =>
      apiFetcher(`/api/v1/cash-register/monthly-summary?year=${d.getFullYear()}&month=${String(d.getMonth() + 1).padStart(2, '0')}`).then(
        (s: CashSummary) => amount(s?.total?.totalTaxFree),
      ),
    ),
  )

/** Starý backend endpoint nezná. Přihlášení a oprávnění ale záložním voláním neobcházíme. */
const shouldFallBack = (e: unknown) => {
  const status = (e as { response?: { status?: number } })?.response?.status
  return status !== 401 && status !== 403
}

/**
 * Tržby z pokladny po měsících (bez DPH, aby seděly s fakturami v dashboard-stats). null = nedostupné.
 * Všechny měsíce jedním požadavkem (monthly-summaries). Dokud ho backend nemá, spadne zpátky na požadavek za každý měsíc.
 */
export const useMonthlyReceipts = (months: Date[]) => {
  const key = ['dashboard-receipts', ...months.map(monthKey)]
  const { data, error, isLoading } = useSWR<number[]>(key, async () => {
    if (months.length === 0) return []
    try {
      const res: { data: (CashSummary & { month: string })[] } = await apiFetcher(
        `/api/v1/cash-register/monthly-summaries?from=${monthKey(months[0])}&to=${monthKey(months[months.length - 1])}`,
      )
      const byMonth = new Map(res.data.map((row) => [row.month, amount(row.total?.totalTaxFree)]))
      return months.map((d) => byMonth.get(monthKey(d)) ?? 0)
    } catch (e) {
      if (shouldFallBack(e)) return fetchMonthByMonth(months)
      throw e
    }
  })
  return { receipts: error ? null : data, isLoading }
}

export const buildMonthRows = (stats: DashboardStats, months: Date[], receipts: number[] | null | undefined): MonthRow[] =>
  months.map((date, i) => {
    const paid = amount(stats.receivedLast12MonthsByMonth[i])
    const r = receipts?.[i] ?? 0
    const expenses = Math.abs(amount(stats.expensesLast12MonthsByMonth[i]))
    return {
      date,
      paid,
      unpaid: amount(stats.notPaidLast12MonthsByMonth[i]),
      receipts: r,
      expenses,
      income: paid + r,
      result: paid + r - expenses,
      paidPrev: amount(stats.receivedLast12MonthsYearAgoByMonth[i]),
      unpaidPrev: amount(stats.notPaidLast12MonthsYearAgoByMonth[i]),
    }
  })

export interface PeriodTotals {
  paid: number
  issued: number
  /** null = za toto období tržby z pokladny neumíme spočítat */
  receipts: number | null
  expenses: number
  expensesPaid: number | null
  income: number
  net: number
}

/** Souhrny za vybrané období. Faktury bereme z dashboard-stats, kde existují, jinak sčítáme měsíce. */
export const periodTotals = (stats: DashboardStats, rows: MonthRow[], period: Period, hasReceipts: boolean): PeriodTotals => {
  const now = new Date()
  const last = rows[rows.length - 1]
  const thisYear = rows.filter((r) => r.date.getFullYear() === now.getFullYear())
  const sum = (list: MonthRow[], f: (r: MonthRow) => number) => list.reduce((s, r) => s + f(r), 0)

  let paid: number, issued: number, receipts: number | null, expenses: number, expensesPaid: number | null
  switch (period) {
    case 'month':
      paid = amount(stats.receivedThisMonth)
      issued = amount(stats.issuedThisMonth)
      receipts = hasReceipts ? last?.receipts ?? 0 : null
      expenses = last?.expenses ?? 0
      expensesPaid = null
      break
    case '30d':
      paid = amount(stats.receivedLast30Days)
      issued = amount(stats.issuedLast30Days)
      receipts = null
      expenses = Math.abs(amount(stats.expensesLast30Days))
      expensesPaid = Math.abs(amount(stats.expensesPaidLast30Days))
      break
    case 'year':
      paid = amount(stats.receivedThisYear)
      issued = amount(stats.issuedThisYear)
      receipts = hasReceipts ? sum(thisYear, (r) => r.receipts) : null
      expenses = sum(thisYear, (r) => r.expenses)
      expensesPaid = null
      break
    case '12m':
    default:
      paid = amount(stats.receivedLast12Months)
      issued = amount(stats.issuedLast12Months)
      receipts = hasReceipts ? sum(rows.slice(1), (r) => r.receipts) : null
      expenses = Math.abs(amount(stats.expensesLast12Months))
      expensesPaid = Math.abs(amount(stats.expensesPaidLast12Months))
  }
  const income = paid + (receipts ?? 0)
  return { paid, issued, receipts, expenses, expensesPaid, income, net: income - expenses }
}

/** Začátek období pro filtrování dokladů v žebříčcích. */
export const periodStart = (period: Period, now = new Date()) => {
  switch (period) {
    case 'month':
      return new Date(now.getFullYear(), now.getMonth(), 1)
    case '30d':
      return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30)
    case 'year':
      return new Date(now.getFullYear(), 0, 1)
    case '12m':
    default:
      return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
  }
}

export interface Ranked {
  id: string | null
  name: string
  count: number
  total: number
}

const taxFree = (doc: Invoice) => (doc.items ?? []).reduce((s, it) => s + Number(it.totalPriceTaxFree?.amount ?? 0), 0)
const currencyOf = (doc: Invoice) => (doc as { currency?: string }).currency ?? doc.items?.[0]?.totalPrice?.currency ?? 'CZK'

/** Seřazení protistran podle součtu dokladů (bez DPH, jen hlavní měna, bez zrušených). */
export const rankCounterparties = (
  docs: Invoice[] | undefined,
  side: 'customer' | 'supplier',
  from: Date,
  currency: string,
): Ranked[] => {
  const map = new Map<string, Ranked>()
  for (const d of docs ?? []) {
    if (d.status === 'cancelled' || d.status === 'refunded') continue
    if (!d.issuedAt || new Date(d.issuedAt) < from) continue
    if (currencyOf(d) !== currency) continue
    const p = side === 'customer' ? d.customer : d.supplier
    const name = p?.name?.trim() || 'Bez názvu'
    const key = p?.id ?? name
    const row = map.get(key) ?? { id: p?.id ?? null, name, count: 0, total: 0 }
    row.count += 1
    row.total += taxFree(d)
    map.set(key, row)
  }
  return Array.from(map.values()).sort((a, b) => b.total - a.total)
}

/** Doklady za letošek a loňský rok (12 měsíců zasahuje do dvou let). */
export const useDocsForRanking = (api: 'invoices' | 'expenses') => {
  const y = new Date().getFullYear()
  const cur = useSWR<Invoice[]>(`/api/v2/${api}?year=${y}&limit=1000`, apiFetcher)
  const prev = useSWR<Invoice[]>(`/api/v2/${api}?year=${y - 1}&limit=1000`, apiFetcher)
  const docs = useMemo(() => [...(cur.data ?? []), ...(prev.data ?? [])], [cur.data, prev.data])
  return { docs, isLoading: cur.isLoading || prev.isLoading }
}
